import {
  useTripPickupOperators,
  useTripPickupChecklist,
  type PickupOperator,
} from "@/api/trips";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ClipboardCheck, Check, X } from "lucide-react";
import { useMemo } from "react";

interface PickupChecklistDialogProps {
  tripId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PickupChecklistDialog({
  tripId,
  isOpen,
  onOpenChange,
}: PickupChecklistDialogProps) {
  const { data, isLoading, error } = useTripPickupOperators(tripId);
  const { data: checklistData } = useTripPickupChecklist(tripId);

  const checklistMap = useMemo(() => {
    const map = new Map<string, "COLLECTED" | "MISSING">();
    checklistData?.items?.forEach((item) => {
      map.set(item.shipmentId, item.status);
    });
    return map;
  }, [checklistData]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Pickup Checklist
          </DialogTitle>
          <DialogDescription className="sr-only">
            Read-only pickup provenance and Shipment collection status for this
            trip.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto pr-1 max-h-[calc(80vh-120px)]">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="p-4 text-center text-red-500">
              Failed to load pickup checklist
            </div>
          )}

          {checklistData?.legacyItems?.length ? (
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Earlier order-level results remain unresolved by Shipment:
              <ul>
                {checklistData.legacyItems.map((item) => (
                  <li key={item.orderId}>
                    ORD-{item.orderNumber}: {item.status.toLowerCase()}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {data?.operators && (
            <>
              {data.operators.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No pickup items found for this trip
                </div>
              ) : (
                <div className="space-y-4">
                  {data.operators.map((item: PickupOperator) => (
                    <div
                      key={item.operator?.id ?? "unknown"}
                      className="rounded border p-4 bg-slate-50"
                    >
                      <div className="text-xs text-muted-foreground uppercase tracking-wide">
                        Picked up by
                      </div>
                      <div className="font-medium text-lg">
                        {item.operator?.name ?? "Pickup not recorded"}
                      </div>
                      <div className="space-y-3 mt-3">
                        {item.senderGroups.map((group) => (
                          <div
                            key={group.senderName}
                            className="border-t pt-2 first:border-t-0 first:pt-0"
                          >
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">
                              Sender
                            </div>
                            <div className="text-sm font-medium mb-2">
                              {group.senderName || "Unknown Sender"}
                            </div>
                            <div className="space-y-1.5">
                              {group.shipments.map((shipment) => {
                                const status = checklistMap.get(shipment.id);
                                return (
                                  <div
                                    key={shipment.id}
                                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-md text-sm ${
                                      status === "MISSING"
                                        ? "bg-red-50 border border-red-200"
                                        : status === "COLLECTED"
                                          ? "bg-green-50 border border-green-200"
                                          : "bg-blue-50 border border-blue-200"
                                    }`}
                                  >
                                    <div>
                                      <span className="font-medium">
                                        SHP-{shipment.shipmentNumber}
                                      </span>
                                      <span className="text-muted-foreground ml-2">
                                        ORD-{shipment.orderNumber} ·{" "}
                                        {shipment.packageCount} pkg
                                        {shipment.packageCount !== 1 ? "s" : ""}
                                      </span>
                                    </div>
                                    {status === "COLLECTED" && (
                                      <span className="flex items-center gap-1 text-green-700 text-xs font-medium">
                                        <Check className="h-3.5 w-3.5" />{" "}
                                        Collected
                                      </span>
                                    )}
                                    {status === "MISSING" && (
                                      <span className="flex items-center gap-1 text-red-700 text-xs font-medium">
                                        <X className="h-3.5 w-3.5" /> Missing
                                      </span>
                                    )}
                                    {!status && (
                                      <span className="text-muted-foreground text-xs">
                                        Pending
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
